'use client';

import { Link } from '@/i18n/navigation';
import { useTranslations } from 'next-intl';
import { LanguageNav } from './LanguageNav';
import { EkutuQrLogo } from './EkutuQrLogo';



export default function Footer({ hideLanguageNav }: { hideLanguageNav?: boolean } = {}) {
	const t = useTranslations('footer');
	const currentYear = new Date().getFullYear();

	return (
		<footer className="w-full max-w-full from-white to-white/60 bg-linear-to-br text-black overflow-x-hidden">
			<div className="mx-auto max-w-7xl px-6 lg:px-8 pt-16 pb-8">
				{/* Main grid */}
				<div className="grid grid-cols-2 md:grid-cols-4 gap-8 pb-12 border-b border-gray-300">
					{/* Logo + tagline */}
					<div className="col-span-2 md:col-span-1">
						<div className="mb-3 text-black">
							<EkutuQrLogo size="default" />
						</div>
						<p className="text-sm text-gray-600 leading-relaxed">{t('tagline')}</p>

					</div>

					{/* Product */}
					<div>
						<h3 className="text-base font-bold text-black mb-4">{t('product')}</h3>
						<ul className="space-y-2.5">
							<li>
								<Link
									href="/features"
									title={t('features')}
									aria-label={t('features')}
									className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
								>
									{t('features')}
								</Link>
							</li>
							<li>
								<Link
									href="/plans"
									title={t('pricing')}
									aria-label={t('pricing')}
									className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
								>
									{t('pricing')}
								</Link>
							</li>
							<li>
								<Link
									href="/products/url-shortener"
									title={t('urlShortener')}
									aria-label={t('urlShortener')}
									className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
								>
									{t('urlShortener')}
								</Link>
							</li>
							<li>
								<Link
									href="/products/qr-codes"
									title={t('qrCodes')}
									aria-label={t('qrCodes')}
									className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
								>
									{t('qrCodes')}
								</Link>
							</li>
							<li>
								<Link
									href="/products/analytics"
									title={t('analyticsProduct')}
									aria-label={t('analyticsProduct')}
									className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
								>
									{t('analyticsProduct')}
								</Link>
							</li>
						</ul>
					</div>

					{/* Resources */}
					<div>
						<h3 className="text-base font-bold text-black mb-4">{t('resources')}</h3>
						<ul className="space-y-2.5">
							<li>
								<Link
									href="/guides/print-checklist"
									title="Print Checklist"
									aria-label="Print Checklist"
									className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
								>
									Print Checklist
								</Link>
							</li>
							<li>
								<Link
									href="/faq"
									title={t('faqLink')}
									aria-label={t('faqLink')}
									className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
								>
									{t('faqLink')}
								</Link>
							</li>
							<li>
								<a
									href="/docs"
									title={t('docs')}
									aria-label={t('docs')}
									className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
								>
									{t('docs')}
								</a>
							</li>

						</ul>
					</div>

					{/* Legal */}
					<div className="space-y-8">
						<div>
							<h3 className="text-base font-bold text-black mb-4">{t('legal')}</h3>
							<ul className="space-y-2.5">
								<li>
									<Link
										href="/imprint"
										title={t('legalNotice')}
										aria-label={t('legalNotice')}
										className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
									>
										{t('legalNotice')}
									</Link>
								</li>
								<li>
									<Link
										href="/privacy-policy"
										title={t('privacyPolicy')}
										aria-label={t('privacyPolicy')}
										className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
									>
										{t('privacyPolicy')}
									</Link>
								</li>
								<li>
									<Link
										href="/terms-of-service"
										title={t('termsOfService')}
										aria-label={t('termsOfService')}
										className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
									>
										{t('termsOfService')}
									</Link>
								</li>
							</ul>
						</div>

						<div>
							<a
								href="mailto:support_qrcode@ekutulimited.com"
								className="text-base font-bold text-black hover:text-gray-700 transition-colors"
							>
								{t('contact')}
							</a>
						</div>
					</div>
				</div>

				{/* Bottom bar */}
				<div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-8">
					<p className="text-xs text-gray-500">
						&copy; Ekutu Limited {currentYear} &mdash; {t('qrCodeCopyright')}
					</p>
					{!hideLanguageNav && <LanguageNav direction="up" />}
				</div>
			</div>
		</footer>
	);
}
